// frontend/src/pages/EventPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";
import LoadingSpinner from "../components/common/LoadingSpinner";
import VideoPlayer from "../components/video/VideoPlayer";
import EventTypeBadge from "../components/event/EventTypeBadge";
import EventFormatBadge from "../components/event/EventFormatBadge";
import EventRegistrationForm from "../components/event/EventRegistrationForm";
import CalendarButton from "../components/event/CalendarButton";
import {
  Calendar,
  Clock,
  MapPin,
  Video,
  Presentation,
  ExternalLink,
  Lock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Users,
  User,
  Coins,
  Play,
} from "lucide-react";
import {
  getEventTimeDisplay,
  getEventLiveStatus,
} from "../utils/eventTimeUtils";

const EventPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { language, t } = useLanguage();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [notification, setNotification] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    fetchEvent();
  }, [slug, language]);

  // Auto-refresh time status every 30s when event is within 30 min or live
  useEffect(() => {
    if (!event) return;
    const start = new Date(event.start_datetime);
    const end = new Date(event.end_datetime);
    const msUntilStart = start - new Date();
    if (msUntilStart <= 30 * 60 * 1000 && new Date() < end) {
      const interval = setInterval(() => setNow(new Date()), 30000);
      return () => clearInterval(interval);
    }
  }, [event?.start_datetime, event?.end_datetime]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getEvent(slug, language);

      if (response.success) {
        setEvent(response.data);
      } else {
        setError(response.message || "Event not found");
      }
    } catch (err) {
      console.error("Error fetching event:", err);
      setError("Failed to load event. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (participationType) => {
    if (!isAuthenticated) {
      navigate(`/login?returnUrl=/event/${slug}`);
      return;
    }

    setRegistering(true);
    setNotification(null);

    // Open window immediately (before await) to avoid popup blockers
    // when event is live or starting soon
    const { isLive: liveNow, isStartingSoon: startingSoonNow } =
      getEventLiveStatus(event);
    const shouldAutoJoin = liveNow || startingSoonNow;
    const newWindow = shouldAutoJoin ? window.open("", "_blank") : null;

    try {
      const response = await apiService.registerForEvent(slug, participationType);

      if (response.success) {
        setNotification({
          type: "success",
          message: response.message || t({ de: "Erfolgreich angemeldet!", en: "Successfully registered!" }),
        });

        // Immediately update event state with meeting_url from response
        if (response.data?.meeting_url) {
          setEvent((prev) => ({
            ...prev,
            meeting_url: response.data.meeting_url,
            meeting_password: response.data.meeting_password,
            participant: {
              source: response.data.source,
              status: response.data.status,
              participationType: response.data.participation_type,
            },
          }));
          // Auto-open meeting link for live/starting-soon events
          if (newWindow && response.data.meeting_url) {
            newWindow.location = response.data.meeting_url;
          }
        } else {
          if (newWindow) newWindow.close();
          fetchEvent();
        }
      } else {
        if (newWindow) newWindow.close();
        setNotification({
          type: "error",
          message: response.message || t({ de: "Anmeldung fehlgeschlagen", en: "Registration failed" }),
        });
      }
    } catch (err) {
      if (newWindow) newWindow.close();
      console.error("Error registering:", err);
      setNotification({
        type: "error",
        message: t({ de: "Anmeldung fehlgeschlagen", en: "Registration failed" }),
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!window.confirm(t({ de: "Möchtest du deine Anmeldung wirklich stornieren?", en: "Are you sure you want to cancel your registration?" }))) {
      return;
    }

    setCancelling(true);
    setNotification(null);

    try {
      const response = await apiService.cancelEventRegistration(slug);

      if (response.success) {
        setNotification({
          type: "success",
          message: t({ de: "Anmeldung storniert", en: "Registration cancelled" }),
        });
        fetchEvent();
      } else {
        setNotification({
          type: "error",
          message: response.message || t({ de: "Stornierung fehlgeschlagen", en: "Cancellation failed" }),
        });
      }
    } catch (err) {
      console.error("Error cancelling:", err);
      setNotification({
        type: "error",
        message: t({ de: "Stornierung fehlgeschlagen", en: "Cancellation failed" }),
      });
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(language === "de" ? "de-DE" : "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: event?.timezone || "Europe/Zurich",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t({ de: "Event nicht gefunden", en: "Event not found" })}
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link
            to="/events"
            className="inline-flex items-center gap-2 text-[#00A3E0] hover:underline"
          >
            <ArrowLeft className="w-5 h-5" />
            {t({ de: "Zurück zu Events", en: "Back to Events" })}
          </Link>
        </div>
      </div>
    );
  }

  const isRegistered = event.participant?.status === "confirmed";
  const isInvited =
    event.participant?.source === "invitation" &&
    event.participant?.status === "pending";
  const { isLive, isStartingSoon, isPast, minutesUntilStart } =
    getEventLiveStatus(event, now);

  // Contract badge colors
  const contractColors = {
    Free: "bg-green-500",
    Registered: "bg-blue-500",
    Basic: "bg-[#23CEE7]",
    Premium: "bg-[#FC0058]",
    Pro: "bg-black",
    Invite: "bg-purple-600",
  };

  const timeDisplay = getEventTimeDisplay(event, language);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <Link
          to="/events"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-[#002855]"
        >
          <ArrowLeft className="w-5 h-5" />
          {t({ de: "Alle Events", en: "All Events" })}
        </Link>
      </div>

      {/* Hero Image */}
      <div className="max-w-4xl mx-auto px-4 mt-4">
        <div className="relative aspect-video bg-gradient-to-br from-[#002855] to-[#00A3E0] rounded-xl overflow-hidden">
          {event.imageUrl && (
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}

          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            <EventFormatBadge format={event.format} />
            <EventTypeBadge type={event.event_type} />
          </div>

          <div
            className={`absolute top-4 right-4 ${
              contractColors[event.access_type] || "bg-gray-500"
            } text-white text-sm font-bold px-3 py-1 rounded`}
          >
            {event.access_type}
          </div>

          {/* Status indicator */}
          {isLive ? (
            <div className="absolute bottom-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md font-bold text-lg flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
              </span>
              LIVE
            </div>
          ) : isStartingSoon ? (
            <div className="absolute bottom-4 right-4 bg-orange-500 text-white px-4 py-2 rounded-md font-bold">
              {t({
                de: `Startet in ${minutesUntilStart} Min.`,
                en: `Starts in ${minutesUntilStart} min`,
              })}
            </div>
          ) : isPast ? (
            <div className="absolute bottom-4 right-4 bg-gray-800 text-white px-3 py-1 rounded-md">
              {t({ de: "Beendet", en: "Ended" })}
            </div>
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <h1 className="text-3xl font-bold text-[#002855]">{event.title}</h1>

            {/* Abstract */}
            {event.abstract && (
              <p className="text-lg text-gray-600">{event.abstract}</p>
            )}

            {/* Trailer Video */}
            {event.trailer && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  {t({ de: "Vorschau", en: "Preview" })}
                </h3>
                {event.trailer.videoUrl ? (
                  <div className="rounded-lg overflow-hidden">
                    <VideoPlayer
                      videoUrl={event.trailer.videoUrl}
                      posterUrl={event.trailer.thumbnailUrl}
                      title={t(event.trailer.title)}
                      subtitles={event.trailer.subtitles}
                      videoLanguage={event.trailer.language}
                    />
                  </div>
                ) : (
                  <Link
                    to={`/video/${event.trailer.slug}`}
                    className="text-[#00A3E0] hover:underline"
                  >
                    {t(event.trailer.title)}
                  </Link>
                )}
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-3">
                  {t({ de: "Beschreibung", en: "Description" })}
                </h3>
                <div
                  className="prose prose-sm max-w-none text-gray-600"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </div>
            )}

            {/* Recording (for past events) */}
            {isPast && event.recording && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  {t({ de: "Aufzeichnung", en: "Recording" })}
                </h3>
                {event.recording.type === "video" && (
                  <Link
                    to={`/video/${event.recording.slug}`}
                    className="text-[#00A3E0] hover:underline"
                  >
                    {t(event.recording.title)}
                  </Link>
                )}
                {event.recording.type === "playlist" && (
                  <Link
                    to={`/playlist/${event.recording.slug}`}
                    className="text-[#00A3E0] hover:underline"
                  >
                    {t(event.recording.title)}
                  </Link>
                )}
                {event.recording.type === "external" && (
                  <a
                    href={event.recording.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#00A3E0] hover:underline"
                  >
                    {t({ de: "Aufzeichnung ansehen", en: "Watch Recording" })}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}

            {/* Notification */}
            {notification && (
              <div
                className={`rounded-lg p-4 ${
                  notification.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  {notification.type === "success" ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  {notification.message}
                </div>
              </div>
            )}

            {/* Registration Section - hide for past events when user has access */}
            {!(isPast && event.hasAccess) && (
              <div className="bg-white rounded-lg shadow p-6">
                {isRegistered ? (
                  // Already registered
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-600 font-semibold">
                      <CheckCircle className="w-5 h-5" />
                      {t({ de: "Du bist angemeldet!", en: "You're registered!" })}
                    </div>

                    {event.participant?.participationType && (
                      <div className="text-sm text-gray-600">
                        {t({ de: "Teilnahme:", en: "Participation:" })}{" "}
                        {event.participant.participationType === "online"
                          ? t({ de: "Online", en: "Online" })
                          : `${t({ de: "Vor Ort", en: "In-Person" })}${event.location ? ` (${event.location})` : ""}`}
                      </div>
                    )}

                    {/* Meeting Link */}
                    {event.meeting_url && (
                      <>
                        {isStartingSoon && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                            <p className="text-orange-700 font-semibold text-sm">
                              {t({
                                de: `Event startet in ${minutesUntilStart} Minuten`,
                                en: `Event starts in ${minutesUntilStart} minutes`,
                              })}
                            </p>
                          </div>
                        )}
                        <a
                          href={event.meeting_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors ${
                            isLive
                              ? "bg-[#EE005F] text-white px-4 py-4 text-lg font-bold hover:bg-[#cc0050] animate-pulse"
                              : "bg-[#00A3E0] text-white px-4 py-3 hover:bg-[#0090c5]"
                          }`}
                        >
                          <Presentation className={isLive ? "w-6 h-6" : "w-5 h-5"} />
                          {isPast
                            ? t({ de: "Link öffnen", en: "Open Link" })
                            : isLive
                            ? t({ de: "JETZT BEITRETEN", en: "JOIN NOW" })
                            : t({ de: "Event beitreten", en: "Join Event" })}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </>
                    )}

                    {/* Calendar Download */}
                    <CalendarButton eventSlug={event.slug} className="w-full" />

                    {/* Cancel Registration */}
                    <button
                      onClick={handleCancelRegistration}
                      disabled={cancelling}
                      className="w-full text-red-600 hover:text-red-700 text-sm py-2"
                    >
                      {cancelling
                        ? t({ de: "Storniere...", en: "Cancelling..." })
                        : t({ de: "Anmeldung stornieren", en: "Cancel Registration" })}
                    </button>
                  </div>
                ) : !event.hasAccess ? (
                  // No access
                  <div className="text-center space-y-4">
                    <Lock className="w-12 h-12 text-gray-300 mx-auto" />
                    <p className="text-gray-600">
                      {event.accessReason === "login_required" &&
                      event.access_type === "Invite"
                        ? t({
                            de: "Dieses Event ist nur auf Einladung. Melde dich an, um zu prüfen, ob du eine Einladung hast.",
                            en: "This event is invite-only. Log in to check if you have an invitation.",
                          })
                        : event.accessReason === "login_required"
                        ? t({
                            de: "Bitte melde dich an, um dich zu registrieren",
                            en: "Please log in to register",
                          })
                        : event.accessReason === "invite_required"
                        ? t({
                            de: "Dieses Event ist nur auf Einladung",
                            en: "This event is invite-only",
                          })
                        : t({
                            de: `${event.access_type}-Abo erforderlich`,
                            en: `${event.access_type} subscription required`,
                          })}
                    </p>
                    {event.accessReason === "login_required" && (
                      <Link
                        to={`/login?returnUrl=/event/${event.slug}`}
                        className="inline-block bg-[#EE005F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors"
                      >
                        {t({ de: "Anmelden", en: "Log In" })}
                      </Link>
                    )}
                  </div>
                ) : isInvited ? (
                  // Invited but not confirmed
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-purple-600 font-semibold">
                      {t({ de: "Du bist eingeladen!", en: "You're invited!" })}
                    </div>
                    <EventRegistrationForm
                      event={event}
                      onRegister={handleRegister}
                      loading={registering}
                      isUrgent={isLive || isStartingSoon}
                    />
                  </div>
                ) : (
                  // Can register
                  <>
                    {isLive && (
                      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4 text-center">
                        <div className="flex items-center justify-center gap-2 text-red-700 font-bold mb-1">
                          <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                          </span>
                          {t({ de: "Dieses Event ist jetzt LIVE!", en: "This event is LIVE now!" })}
                        </div>
                        <p className="text-red-600 text-sm">
                          {t({ de: "Melde dich an, um sofort beizutreten", en: "Register to join immediately" })}
                        </p>
                      </div>
                    )}
                    {isStartingSoon && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 text-center">
                        <p className="text-orange-800 font-semibold text-sm">
                          {t({
                            de: `Nur noch ${minutesUntilStart} Minuten bis zum Start!`,
                            en: `Only ${minutesUntilStart} minutes until it starts!`,
                          })}
                        </p>
                      </div>
                    )}
                    <EventRegistrationForm
                      event={event}
                      onRegister={handleRegister}
                      loading={registering}
                      isUrgent={isLive || isStartingSoon}
                    />
                  </>
                )}
              </div>
            )}

            {/* Past event notice */}
            {isPast && (
              <div className="bg-gray-100 rounded-lg p-6 text-center text-gray-600">
                {t({
                  de: "Dieses Event ist bereits beendet",
                  en: "This event has ended",
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Details Card */}
            <div className={`bg-white rounded-lg shadow p-6 space-y-4 ${isPast ? "opacity-75" : ""}`}>
              {/* Date */}
              <div className="flex items-start gap-3">
                <Calendar className={`w-5 h-5 mt-0.5 ${isPast ? "text-gray-400" : "text-[#EE005F]"}`} />
                <div>
                  <div className={`font-medium ${isPast ? "text-gray-500" : "text-gray-800"}`}>
                    {formatDate(event.start_datetime)}
                  </div>
                </div>
              </div>

              {/* Time */}
              <div className="flex items-start gap-3">
                <Clock className={`w-5 h-5 mt-0.5 ${isPast ? "text-gray-400" : "text-[#EE005F]"}`} />
                <div>
                  <div className={`font-medium ${isPast ? "text-gray-500" : "text-gray-800"}`}>
                    {timeDisplay.eventTimeLine.start} -{" "}
                    {timeDisplay.eventTimeLine.end}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      {timeDisplay.eventTimeLine.abbreviation}
                    </span>
                  </div>
                  {timeDisplay.showLocalTime && timeDisplay.localTimeLine && (
                    <div className="text-sm text-gray-500">
                      {timeDisplay.localTimeLine.start} -{" "}
                      {timeDisplay.localTimeLine.end}{" "}
                      {timeDisplay.localTimeLine.abbreviation}{" "}
                      <span className="text-xs">
                        ({timeDisplay.localTimeLine.label})
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Location (for in-person/hybrid) */}
              {event.location && event.format !== "online" && (
                <div className="flex items-start gap-3">
                  <MapPin className={`w-5 h-5 mt-0.5 ${isPast ? "text-gray-400" : "text-[#EE005F]"}`} />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`font-medium transition-colors ${isPast ? "text-gray-500 hover:text-gray-700 hover:underline" : "text-gray-800 hover:text-[#EE005F] hover:underline"}`}
                  >
                    {event.location}
                  </a>
                </div>
              )}

              {/* Capacity */}
              {(event.capacity?.max_online || event.capacity?.max_in_person) && (
                <div className="flex items-start gap-3">
                  <Users className={`w-5 h-5 mt-0.5 ${isPast ? "text-gray-400" : "text-[#EE005F]"}`} />
                  <div className="text-sm text-gray-600">
                    {event.format === "hybrid" ? (
                      <>
                        {event.capacity.spotsRemaining?.online !== null && (
                          <div>
                            {event.capacity.spotsRemaining.online} /{" "}
                            {event.capacity.max_online}{" "}
                            {t({ de: "Online-Plätze", en: "online spots" })}
                          </div>
                        )}
                        {event.capacity.spotsRemaining?.in_person !== null && (
                          <div>
                            {event.capacity.spotsRemaining.in_person} /{" "}
                            {event.capacity.max_in_person}{" "}
                            {t({ de: "Vor-Ort-Plätze", en: "in-person spots" })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        {event.capacity.spotsRemaining?.online !== null
                          ? `${event.capacity.spotsRemaining.online} / ${event.capacity.max_online}`
                          : `${event.capacity.spotsRemaining?.in_person} / ${event.capacity.max_in_person}`}{" "}
                        {t({ de: "Plätze verfügbar", en: "spots available" })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pricing */}
              {event.pricing_type === "donation" && (
                <div className="flex items-start gap-3">
                  <Coins className={`w-5 h-5 mt-0.5 ${isPast ? "text-gray-400" : "text-[#EE005F]"}`} />
                  <div>
                    <div className={`font-medium ${isPast ? "text-gray-500" : "text-gray-800"}`}>
                      {t({ de: "Kollekte", en: "Donation" })}
                    </div>
                    {event.suggested_donation && (
                      <div className="text-sm text-gray-500">
                        {event.donation_currency} {event.suggested_donation}{" "}
                        {t({ de: "empfohlen", en: "suggested" })}
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* Speaker */}
            {event.speaker && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#EE005F]" />
                  {t({ de: "Referent", en: "Speaker" })}
                </h3>
                <div className="flex items-start gap-4">
                  {event.speaker.imageUrl && (
                    <img
                      src={event.speaker.imageUrl}
                      alt={event.speaker.name}
                      className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div>
                    <div className="font-medium text-gray-800">{event.speaker.name}</div>
                    {event.speaker.bio && (
                      <p className="text-sm text-gray-600 mt-1">{event.speaker.bio}</p>
                    )}
                    {event.speaker.url && (
                      <a
                        href={event.speaker.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[#00A3E0] hover:underline mt-2"
                      >
                        {t({ de: "Mehr erfahren", en: "Learn more" })}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

    </div>
  );
};

export default EventPage;
